import React, { type ComponentType } from 'react';
import { graphql, useFragment } from 'react-relay';
import type { CSSToken } from '@atlaskit/tokens';
import { isVisualRefreshEnabled } from '@atlassian/jira-visual-refresh-rollout/src/feature-switch/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import type {
	IssueCountProps,
	FooterProps,
	TopLevelIssueCreateConfig,
} from '../../common/types.tsx';
import { Footer as DefaultFooter } from '../../common/ui/components/footer/index.tsx';
import {
	useIsInfiniteScrollEnabled,
	useIsIssueCreateEnabled,
} from '../../controllers/features/selectors.tsx';
import type { FooterRenderer$key as IssuesKey } from './__generated__/FooterRenderer.graphql';
import type { FooterRenderer_rank$key as RankFieldFragment } from './__generated__/FooterRenderer_rank.graphql';
import IssueCountRenderer from './issue-count-renderer/IssueCountRenderer.tsx';
import { IssueCreateFooter } from './issue-create-footer/IssueCreateFooter.tsx';
import { PaginationControls } from './pagination-controls/PaginationControls.tsx';

export type Props = {
	/**
	 * Overridable Footer component to render.
	 */
	Footer?: ComponentType<FooterProps>;
	/**
	 * Overridable IssueCount component to render.
	 */
	IssueCount?: ComponentType<IssueCountProps>;
	/**
	 * Relay fragment key for a connection of issues.
	 */
	issues: IssuesKey;
	/**
	 * Relay fragment key for the rank field.
	 */
	rankField: RankFieldFragment | null;
	/**
	 * Callback invoked when a user action results in a need to navigate to a different page of issues. This callback
	 * will be invoked with the cursor to paginate to, and whether last issue in the result set should be selected (for
	 * example, if the user is trying to navigate from the first issue in a page to the last issue in the previous page).
	 */
	onPageChange?: (cursor: string, shouldSelectLastIssue: boolean) => void;
	/**
	 * Controls whether the additional fields can be shown in the inline create footer.
	 */
	// TODO: Make this required when refactor_issue_table_for_timline is being cleaned up
	canShowAdditionalFields?: boolean;
	/**
	 * The border radius of the inline create footer.
	 */
	footerBorderRadius?: CSSToken;
	/**
	 * Configuration to limit the allowed issue types users can create at the top level.
	 */
	topLevelIssueCreateConfig?: TopLevelIssueCreateConfig;
	/**
	 * Controls whether to make space for the global rovo button which is situated in the bottom right hand corner of the page.
	 */
	isMakeSpaceForGlobalRovoButtonEnabled?: boolean;
};

/**
 * Render the table footer, composing together the default IssueCount and Pagination UI elements.
 */
const FooterRenderer = ({
	Footer,
	IssueCount,
	issues,
	rankField,
	onPageChange,
	canShowAdditionalFields,
	footerBorderRadius,
	topLevelIssueCreateConfig,
	isMakeSpaceForGlobalRovoButtonEnabled = false,
}: Props) => {
	const issueData = useFragment<IssuesKey>(
		graphql`
			fragment FooterRenderer on JiraIssueConnection
			@argumentDefinitions(pageSize: { type: "Int" }) {
				...IssueCountRenderer
				...IssueCreateFooter
				...PaginationControls @arguments(pageSize: $pageSize)
				totalIssueSearchResultCount
			}
		`,
		issues,
	);

	const rankFieldData = useFragment<RankFieldFragment>(
		graphql`
			fragment FooterRenderer_rank on JiraJqlFieldWithAliases {
				...IssueCreateFooter_rankField
			}
		`,
		rankField ?? null,
	);

	const isIssueCreateEnabled = useIsIssueCreateEnabled();
	const isInfiniteScrollEnabled = useIsInfiniteScrollEnabled();

	// Use new Visual Refresh footer order if visual refresh or any m1 features are enabled to
	// simplfiy the number of cases we need to handle with the new features.
	const isNewFooterOrderEnabled =
		isVisualRefreshEnabled() || isIssueCreateEnabled || isInfiniteScrollEnabled;

	let start;
	let center;
	let end;

	// Show pagination controls only when discrete pagination is enabled
	if (onPageChange && !isInfiniteScrollEnabled) {
		const paginationControls = (
			<PaginationControls
				issues={issueData}
				onPageChange={(after: string) => onPageChange(after, false)}
				isNewFooterOrderEnabled={isNewFooterOrderEnabled}
			/>
		);

		if (isNewFooterOrderEnabled) {
			center = paginationControls;
		} else {
			end = paginationControls;
		}
	}

	// Show issue count when either pagination or infinite scroll are enabled
	if (onPageChange || isInfiniteScrollEnabled) {
		const moveIssueCountToEnd =
			isMakeSpaceForGlobalRovoButtonEnabled && fg('global_rovo_button_in_jira')
				? center !== undefined
				: isNewFooterOrderEnabled;

		const issueCount = (
			<IssueCountRenderer
				issues={issueData}
				IssueCount={IssueCount}
				isNewFooterOrderEnabled={moveIssueCountToEnd}
				isInfiniteScrollEnabled={isInfiniteScrollEnabled}
			/>
		);

		if (moveIssueCountToEnd) {
			end = issueCount;
		} else {
			center = issueCount;
		}
	}

	const hasIssues = (issueData.totalIssueSearchResultCount ?? 0) > 0;
	const hasIssueConnectionControls = hasIssues && (!!onPageChange || isInfiniteScrollEnabled);
	const hasControlsToDisplay = hasIssueConnectionControls || isIssueCreateEnabled;

	// Hide the footer if we have no issues UNLESS the consumer has provided a custom footer component, in which case
	// the consumer becomes responsible for when the footer should be shown/hidden.
	if (!hasControlsToDisplay && !Footer) {
		return null;
	}

	const FooterToRender = Footer ?? DefaultFooter;

	if (isIssueCreateEnabled) {
		return (
			<IssueCreateFooter
				// @ts-expect-error: to unblock React 18.2.0 -> 18.3.1 version bump
				Footer={FooterToRender}
				rankField={rankFieldData ?? null}
				start={start}
				center={center}
				end={end}
				issues={issueData}
				canShowAdditionalFields={canShowAdditionalFields}
				footerBorderRadius={footerBorderRadius}
				topLevelIssueCreateConfig={topLevelIssueCreateConfig}
			/>
		);
	}
	return <FooterToRender start={start} center={center} end={end} />;
};

export default FooterRenderer;
